import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Plus, FileText, Receipt, Upload, Trash2, Edit } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const emptyInvoice = () => ({
    invoice_month: new Date().toISOString().slice(0, 7),
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    period_from: "",
    period_to: "",
    due_date: "",
    previous_balance: 0,
    current_charges: 0,
    taxable_amount: 0,
    gst_amount: 0,
    other_charges: 0,
    discount: 0,
    total_amount: 0,
    data_used_gb: "",
    voice_minutes_used: "",
    remarks: "",
});

const emptyPayment = () => ({
    invoice_id: "",
    payment_date: new Date().toISOString().slice(0, 10),
    amount: 0,
    payment_mode: "UPI",
    paid_from_bank_account_id: "",
    paid_via_card_id: "",
    reference_number: "",
    posted: true,
    remarks: "",
});

const KV = ({ label, value, mono, className = "" }) => (
    <div className={className}>
        <div className="eyebrow">{label}</div>
        <div className={`text-sm text-stone-900 mt-0.5 ${mono ? "font-mono-figure tabular-nums" : ""}`}>{value ?? "—"}</div>
    </div>
);

export default function BillDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { hasRole } = useAuth();
    const [bill, setBill] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [banks, setBanks] = useState([]);
    const [cards, setCards] = useState([]);
    const [tab, setTab] = useState("invoices");
    const [invOpen, setInvOpen] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const [invForm, setInvForm] = useState(emptyInvoice());
    const [payForm, setPayForm] = useState(emptyPayment());
    const [editingInv, setEditingInv] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get(`/bills/${id}`).then((r) => setBill(r.data));
        api.get(`/bills/${id}/invoices`).then((r) => setInvoices(r.data || []));
        api.get(`/bills/${id}/payments`).then((r) => setPayments(r.data || []));
        api.get("/bank-accounts").then((r) => setBanks(r.data || [])).catch(() => setBanks([]));
        api.get("/cards").then((r) => setCards(r.data || [])).catch(() => setCards([]));
    };
    useEffect(() => { load(); }, [id]);

    const submitInvoice = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const payload = { ...invForm, bill_id: id };
            ["previous_balance", "current_charges", "taxable_amount", "gst_amount",
             "other_charges", "discount", "total_amount", "minimum_amount_due",
             "data_used_gb", "voice_minutes_used"].forEach((k) => {
                payload[k] = payload[k] === "" || payload[k] === null ? null : Number(payload[k]);
             });
            if (editingInv) {
                await api.patch(`/bills/invoices/${editingInv.id}`, payload);
                toast.success("Invoice updated");
            } else {
                await api.post(`/bills/${id}/invoices`, payload);
                toast.success("Invoice recorded");
            }
            setInvOpen(false); setEditingInv(null); setInvForm(emptyInvoice()); load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally { setBusy(false); }
    };

    const submitPayment = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const payload = { ...payForm, bill_id: id };
            payload.amount = Number(payload.amount || 0);
            ["paid_from_bank_account_id", "paid_via_card_id", "invoice_id"].forEach((k) => {
                if (!payload[k]) payload[k] = null;
            });
            await api.post(`/bills/${id}/payments`, payload);
            toast.success("Payment recorded");
            setPayOpen(false); setPayForm(emptyPayment()); load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally { setBusy(false); }
    };

    const uploadInvoiceFile = async (invoiceId, file) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/bills/invoices/${invoiceId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Invoice PDF uploaded");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        }
    };

    const openEditInvoice = (inv) => {
        setEditingInv(inv);
        setInvForm({ ...emptyInvoice(), ...inv });
        setInvOpen(true);
    };
    const deleteInvoice = async (invId) => {
        if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
        try { await api.delete(`/bills/invoices/${invId}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error("Failed to delete"); }
    };
    const deletePayment = async (payId) => {
        if (!window.confirm("Delete this payment?")) return;
        try { await api.delete(`/bills/payments/${payId}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error("Failed to delete"); }
    };

    if (!bill) return <div className="text-stone-500">Loading…</div>;

    const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const totalBilled = invoices.reduce((s, i) => s + Number(i.total_amount || 0), 0);
    const outstanding = Math.max(0, totalBilled - totalPaid);

    return (
        <div data-testid="bill-detail-page">
            <button onClick={() => nav("/bills")} className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-4">
                <ArrowLeft size={14} /> Back to bills
            </button>

            {/* Header */}
            <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: bill.color_tag || "#0369a1" }} />
                        {bill.kind} · {bill.business_personal}
                    </div>
                    <h1 className="font-display text-4xl text-stone-900 leading-none mt-2">
                        {bill.operator}
                    </h1>
                    <div className="mt-2 flex items-center gap-3 text-sm text-stone-600">
                        <span className="font-mono-figure tabular-nums">{bill.masked_account}</span>
                        {bill.account_label && <span className="text-stone-400">· {bill.account_label}</span>}
                        {bill.plan_name && <span className="text-stone-400">· {bill.plan_name}</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {hasRole("admin", "finance") && (
                        <>
                            <Button variant="outline" onClick={() => nav(`/bills/${id}/edit`)} data-testid="bill-edit-btn">
                                <Edit size={13} className="mr-1.5" /> Edit
                            </Button>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" onClick={() => { setEditingInv(null); setInvForm(emptyInvoice()); setInvOpen(true); }} data-testid="bill-add-invoice-btn">
                                <Plus size={13} className="mr-1.5" /> Log invoice
                            </Button>
                            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => { setPayForm(emptyPayment()); setPayOpen(true); }} data-testid="bill-add-payment-btn">
                                <Plus size={13} className="mr-1.5" /> Log payment
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="kpi-card">
                    <div className="eyebrow">Monthly rent</div>
                    <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(bill.monthly_rent)}</div>
                </div>
                <div className="kpi-card">
                    <div className="eyebrow">Total billed (YTD)</div>
                    <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(totalBilled)}</div>
                    <div className="text-[11px] text-stone-500 mt-1">{invoices.length} invoices</div>
                </div>
                <div className="kpi-card">
                    <div className="eyebrow">Paid</div>
                    <div className="font-display text-2xl mt-2 tabular-nums text-emerald-700">{fmtMoney(totalPaid)}</div>
                    <div className="text-[11px] text-stone-500 mt-1">{payments.length} payments</div>
                </div>
                <div className="kpi-card">
                    <div className="eyebrow">Outstanding</div>
                    <div className={`font-display text-2xl mt-2 tabular-nums ${outstanding > 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtMoney(outstanding)}</div>
                </div>
            </div>

            {/* Overview grid */}
            <div className="form-section mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KV label="Holder" value={bill.holder_name} />
                    <KV label="Family member" value={bill.family_member} />
                    <KV label="Circle" value={bill.circle} />
                    <KV label="Frequency" value={bill.frequency} />
                    <KV label="Invoice gen day" value={bill.invoice_gen_day} mono />
                    <KV label="Payment due day" value={bill.payment_due_day} mono />
                    <KV label="Data limit" value={bill.data_limit_gb ? `${bill.data_limit_gb} GB` : "—"} mono />
                    <KV label="Voice mins" value={bill.voice_minutes} mono />
                    <KV label="Auto-debit" value={bill.auto_debit ? "Yes" : "No"} />
                    <KV label="UPI ID" value={bill.upi_id} mono />
                    <KV label="Portal" value={bill.portal_link ? <a href={bill.portal_link} target="_blank" rel="noreferrer" className="text-blue-700 underline">Open</a> : "—"} />
                    <KV label="OTT bundle" value={bill.ott_bundle} />
                    {bill.primary_device_model && <KV label="Device" value={bill.primary_device_model} />}
                    {bill.imei && <KV label="IMEI" value={bill.imei} mono />}
                    {bill.meter_number && <KV label="Meter" value={bill.meter_number} mono />}
                    {bill.consumer_number && <KV label="Consumer #" value={bill.consumer_number} mono />}
                    {bill.plan_expiry_date && <KV label="Plan expiry" value={fmtDate(bill.plan_expiry_date)} mono />}
                    {bill.activation_date && <KV label="Activated" value={fmtDate(bill.activation_date)} mono />}
                    {bill.provider_gstin && <KV label="Provider GSTIN" value={bill.provider_gstin} mono />}
                    {bill.vas_addons?.length > 0 && (
                        <div className="col-span-2 md:col-span-4">
                            <div className="eyebrow">Add-ons</div>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                                {bill.vas_addons.map((v, i) => (
                                    <span key={i} className="text-[11px] px-2 py-0.5 rounded border border-stone-200 bg-stone-50 text-stone-700">{v}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="invoices" data-testid="tab-invoices"><FileText size={12} className="mr-1.5" /> Invoices · {invoices.length}</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="tab-payments"><Receipt size={12} className="mr-1.5" /> Payments · {payments.length}</TabsTrigger>
                </TabsList>

                <TabsContent value="invoices">
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <table className="dense-table w-full">
                            <thead><tr>
                                <th>Month</th>
                                <th>Invoice #</th>
                                <th>Due date</th>
                                <th className="text-right">Total</th>
                                <th className="text-right">Paid</th>
                                <th className="text-right">Outstanding</th>
                                <th>Status</th>
                                <th>Data</th>
                                <th>File</th>
                                <th></th>
                            </tr></thead>
                            <tbody>
                                {invoices.length === 0 && (
                                    <tr><td colSpan={10} className="text-center text-stone-500 py-10">No invoices yet.</td></tr>
                                )}
                                {invoices.map((inv) => (
                                    <tr key={inv.id} data-testid={`bill-invoice-row-${inv.id}`}>
                                        <td className="tabular-nums">{inv.invoice_month}</td>
                                        <td className="text-xs">{inv.invoice_number || "—"}</td>
                                        <td className="tabular-nums text-xs">{fmtDate(inv.due_date)}</td>
                                        <td className="text-right tabular-nums font-medium">{fmtMoney(inv.total_amount)}</td>
                                        <td className="text-right tabular-nums text-emerald-700">{fmtMoney(inv.paid_amount)}</td>
                                        <td className="text-right tabular-nums text-red-700">{fmtMoney(inv.outstanding)}</td>
                                        <td>
                                            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                                                inv.payment_status === "Paid" ? "text-emerald-700 border-emerald-200 bg-emerald-50" :
                                                inv.payment_status === "Partial" ? "text-amber-700 border-amber-200 bg-amber-50" :
                                                "text-red-700 border-red-200 bg-red-50"}`}>
                                                {inv.payment_status}
                                            </span>
                                        </td>
                                        <td className="text-xs">{inv.data_used_gb ? `${inv.data_used_gb} GB` : "—"}</td>
                                        <td>
                                            {inv.invoice_file ? (
                                                <a href={`${BACKEND_URL}${inv.invoice_file}`} target="_blank" rel="noreferrer" className="text-blue-700 text-xs underline">View</a>
                                            ) : hasRole("admin", "finance") ? (
                                                <label className="text-xs text-stone-500 cursor-pointer hover:text-stone-900 inline-flex items-center gap-1">
                                                    <Upload size={11} /> Upload
                                                    <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadInvoiceFile(inv.id, e.target.files[0])} data-testid={`bill-invoice-upload-${inv.id}`} />
                                                </label>
                                            ) : "—"}
                                        </td>
                                        <td>
                                            {hasRole("admin", "finance") && (
                                                <div className="flex items-center justify-end gap-1">
                                                    <button className="text-xs px-2 py-1 rounded hover:bg-stone-100" onClick={() => openEditInvoice(inv)}>Edit</button>
                                                    <button className="p-1 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" onClick={() => deleteInvoice(inv.id)}><Trash2 size={12} /></button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="payments">
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <table className="dense-table w-full">
                            <thead><tr>
                                <th>Date</th>
                                <th>Invoice</th>
                                <th className="text-right">Amount</th>
                                <th>Mode</th>
                                <th>Reference</th>
                                <th>Posted?</th>
                                <th></th>
                            </tr></thead>
                            <tbody>
                                {payments.length === 0 && (
                                    <tr><td colSpan={7} className="text-center text-stone-500 py-10">No payments yet.</td></tr>
                                )}
                                {payments.map((p) => {
                                    const invMonth = invoices.find((i) => i.id === p.invoice_id)?.invoice_month;
                                    return (
                                        <tr key={p.id} data-testid={`bill-payment-row-${p.id}`}>
                                            <td className="tabular-nums text-xs">{fmtDate(p.payment_date)}</td>
                                            <td className="text-xs">{invMonth || <span className="text-stone-400">—</span>}</td>
                                            <td className="text-right tabular-nums font-medium">{fmtMoney(p.amount)}</td>
                                            <td className="text-xs">{p.payment_mode}</td>
                                            <td className="text-xs font-mono-figure tabular-nums">{p.reference_number || "—"}</td>
                                            <td>{p.posted ? <span className="text-emerald-700 text-xs">Yes</span> : <span className="text-amber-700 text-xs">No</span>}</td>
                                            <td>
                                                {hasRole("admin", "finance") && (
                                                    <button className="p-1 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" onClick={() => deletePayment(p.id)}><Trash2 size={12} /></button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Invoice dialog */}
            <Dialog open={invOpen} onOpenChange={(v) => { setInvOpen(v); if (!v) { setEditingInv(null); setInvForm(emptyInvoice()); }}}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editingInv ? "Edit invoice" : "Log invoice"}</DialogTitle></DialogHeader>
                    <form onSubmit={submitInvoice} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div><Label className="eyebrow">Month (YYYY-MM)</Label><Input value={invForm.invoice_month} onChange={(e) => setInvForm((f) => ({ ...f, invoice_month: e.target.value }))} required data-testid="inv-form-month" /></div>
                        <div><Label className="eyebrow">Invoice #</Label><Input value={invForm.invoice_number} onChange={(e) => setInvForm((f) => ({ ...f, invoice_number: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Invoice date</Label><Input type="date" value={invForm.invoice_date} onChange={(e) => setInvForm((f) => ({ ...f, invoice_date: e.target.value }))} required /></div>
                        <div><Label className="eyebrow">Period from</Label><Input type="date" value={invForm.period_from} onChange={(e) => setInvForm((f) => ({ ...f, period_from: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Period to</Label><Input type="date" value={invForm.period_to} onChange={(e) => setInvForm((f) => ({ ...f, period_to: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Due date</Label><Input type="date" value={invForm.due_date} onChange={(e) => setInvForm((f) => ({ ...f, due_date: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Previous balance</Label><Input type="number" value={invForm.previous_balance} onChange={(e) => setInvForm((f) => ({ ...f, previous_balance: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Current charges</Label><Input type="number" value={invForm.current_charges} onChange={(e) => setInvForm((f) => ({ ...f, current_charges: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Taxable amount</Label><Input type="number" value={invForm.taxable_amount} onChange={(e) => setInvForm((f) => ({ ...f, taxable_amount: e.target.value }))} /></div>
                        <div><Label className="eyebrow">GST amount</Label><Input type="number" value={invForm.gst_amount} onChange={(e) => setInvForm((f) => ({ ...f, gst_amount: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Other charges</Label><Input type="number" value={invForm.other_charges} onChange={(e) => setInvForm((f) => ({ ...f, other_charges: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Discount</Label><Input type="number" value={invForm.discount} onChange={(e) => setInvForm((f) => ({ ...f, discount: e.target.value }))} /></div>
                        <div><Label className="eyebrow font-semibold">Total amount ★</Label><Input type="number" value={invForm.total_amount} onChange={(e) => setInvForm((f) => ({ ...f, total_amount: e.target.value }))} required data-testid="inv-form-total" /></div>
                        <div><Label className="eyebrow">Data used (GB)</Label><Input type="number" value={invForm.data_used_gb} onChange={(e) => setInvForm((f) => ({ ...f, data_used_gb: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Voice mins used</Label><Input type="number" value={invForm.voice_minutes_used} onChange={(e) => setInvForm((f) => ({ ...f, voice_minutes_used: e.target.value }))} /></div>
                        <div className="md:col-span-3"><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={invForm.remarks} onChange={(e) => setInvForm((f) => ({ ...f, remarks: e.target.value }))} /></div>
                        <DialogFooter className="md:col-span-3">
                            <Button type="button" variant="outline" onClick={() => setInvOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="inv-form-save">
                                {busy ? "Saving…" : editingInv ? "Save" : "Add invoice"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Payment dialog */}
            <Dialog open={payOpen} onOpenChange={setPayOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Log payment</DialogTitle></DialogHeader>
                    <form onSubmit={submitPayment} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label className="eyebrow">Against invoice</Label>
                            <Select value={payForm.invoice_id || "none"} onValueChange={(v) => setPayForm((f) => ({ ...f, invoice_id: v === "none" ? "" : v }))}>
                                <SelectTrigger data-testid="pay-form-invoice"><SelectValue placeholder="No specific invoice" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— standalone / on-account —</SelectItem>
                                    {invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_month} · {fmtMoney(i.total_amount)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label className="eyebrow">Payment date</Label><Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm((f) => ({ ...f, payment_date: e.target.value }))} required /></div>
                        <div><Label className="eyebrow">Amount (₹)</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} required data-testid="pay-form-amount" /></div>
                        <div>
                            <Label className="eyebrow">Mode</Label>
                            <Select value={payForm.payment_mode} onValueChange={(v) => setPayForm((f) => ({ ...f, payment_mode: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["UPI", "Bank Transfer", "Auto Debit", "Credit Card", "Cash", "Cheque", "Wallet", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="eyebrow">From bank account</Label>
                            <Select value={payForm.paid_from_bank_account_id || "none"} onValueChange={(v) => setPayForm((f) => ({ ...f, paid_from_bank_account_id: v === "none" ? "" : v }))}>
                                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— none —</SelectItem>
                                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · ••{b.last_4}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="eyebrow">Via credit card</Label>
                            <Select value={payForm.paid_via_card_id || "none"} onValueChange={(v) => setPayForm((f) => ({ ...f, paid_via_card_id: v === "none" ? "" : v }))}>
                                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— none —</SelectItem>
                                    {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · ••{c.last_4}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label className="eyebrow">Reference / UTR</Label><Input value={payForm.reference_number} onChange={(e) => setPayForm((f) => ({ ...f, reference_number: e.target.value }))} /></div>
                        <div className="flex items-center gap-3 border border-stone-200 rounded-md px-3 py-2.5">
                            <div className="flex-1">
                                <div className="text-sm text-stone-900">Posted / confirmed</div>
                                <div className="text-[11px] text-stone-500">Bank/provider confirms receipt</div>
                            </div>
                            <input type="checkbox" checked={payForm.posted} onChange={(e) => setPayForm((f) => ({ ...f, posted: e.target.checked }))} />
                        </div>
                        <div className="md:col-span-2"><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={payForm.remarks} onChange={(e) => setPayForm((f) => ({ ...f, remarks: e.target.value }))} /></div>
                        <DialogFooter className="md:col-span-2">
                            <Button type="button" variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="pay-form-save">
                                {busy ? "Saving…" : "Log payment"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
